/**
 * What the targeting section says, where a runner can reach it.
 *
 * 🔴 One sentence here is the most important thing this console says to a buyer,
 * and it is the one that was WRONG in the opposite direction until 2026-10-07: a
 * line item with no targeting criteria is not inert, it is **everyone**. The old
 * copy said X would not serve such a line item, which reassured exactly where it
 * should have warned.
 *
 * It is carried as a **reading of X's documentation**, with the attribution in the
 * sentence itself — `campaign-management`'s Targeting section, read 2026-10-07 —
 * because nobody here has seen a live line item: the sandbox refused every write
 * that day. `targeting-copy.test.ts` asserts the attribution, and the mutation
 * table has a row that removes those words and expects red, because they look like
 * hedging and are the first thing a tidy-up would take.
 */
import type {
	TargetingCriterion,
	TargetingDevice,
	TargetingLanguage,
	TargetingLocation,
	TargetingNetworkOperator,
} from "./api.ts";
import type { StartGate, StatusOutcome } from "./line-items.ts";

/**
 * 🔴 Shown whenever a line item's criteria list comes back empty.
 *
 * Not "no targeting yet", which reads as a blank to be filled in at leisure. The
 * state has a reach, and the sentence names it. Until PR 3 of the one-screen layout
 * (2026-10-29) it ended «Narrow it below, or stop the line item.»: the narrowing is beside
 * the list now, in its own panel, and the switch is Pause (B2).
 */
/**
 * Since targeting v2 (2026-10-29): criteria that only exclude. The guide speaks of a line item
 * with NO criteria (G:573); of one whose criteria only exclude, the reference says nothing, so
 * this names the state and does not say whom it reaches (f5, 30.10). A state banner, never folded.
 */
export const ONLY_EXCLUSIONS = {
	title: "Only exclusions: no criterion narrows this line item to a place it targets.",
	body: "Every criterion here excludes (NE). X's documentation says a line item with no targeting criteria targets all users worldwide; what X does with one whose criteria only exclude, the reference does not say. Add a place to target before starting it.",
};

export const NO_CRITERIA = {
	title: "No targeting: X's documentation says this line item reaches everyone.",
	body: "A line item with no targeting criteria targets all users worldwide, according to X's own documentation (campaign-management, the Targeting section, read 2026-10-07). Nobody here has seen that happen — the sandbox refused every write that day — so it is X's statement rather than something this console has measured. Narrow it in Add criterion, or pause the line item.",
};

/**
 * Beside the narrowing form: what this console can add, and what it cannot.
 *
 * Until 2026-10-23 this was `ONE_TYPE`: «This console adds one kind of criterion: a
 * place. X attaches conditions to the other kinds — a TV show needs a place first, an
 * app list only works as an exclusion, and only one age bucket is allowed per line item
 * — so offering them here would be offering requests that cannot succeed.» Language,
 * gender and age were added for Ads Manager's ad-group form
 * (`audits/2026-10-23/adgroup-fields-spec.md`, PR 2). Until 2026-10-29 it was `FOUR_TYPES`:
 * «This console adds four kinds of criterion: a place, a language, a gender and an age
 * bucket. …» — operating system and device model joined the line item's own panel then
 * (targeting v2), as the wizard had them since PR 3, and a place may be excluded.
 */
export const KINDS_ADDED =
	"This console adds a place (targeted or excluded), a language, a gender, an age bucket, an operating system and a device model as criteria. X attaches conditions to other kinds — a TV show needs a place first, an app list only works as an exclusion — so offering them here would be offering requests that cannot succeed. X allows one age bucket per line item.";

/**
 * GENDER's two values, in the reference's words: «Target males (1) or females (2).
 * Leave null to target all.» (campaign-management, read 2026-10-23). No gender
 * criterion is the «all».
 */
export const GENDER_OPTIONS = [
	{ value: "1", label: "Male" },
	{ value: "2", label: "Female" },
] as const;

/**
 * 🔴 The age buckets offered — the worker's `AGE_BUCKETS` (platform/x-api.ts), where
 * each one's source is written: `AGE_21_TO_34` from the reference's batch example,
 * `AGE_25_TO_54` observed from X on xyc94. X does not publish its list, so a bucket is
 * added there when X shows it, and here with it; `targeting-copy.test.ts` reads the
 * worker's list and holds the two equal.
 */
export const AGE_BUCKETS = ["AGE_21_TO_34", "AGE_25_TO_54"] as const;

/**
 * Beside the age choice: why there are two, where each comes from, and the one-bucket rule.
 *
 * 🔴 Until 2026-10-29 it said «only the ones X has shown: 21–34 and 25–54» — but 21–34 is
 * the reference's example, never seen from X, so «shown» was true of one of the two. And the
 * owner asked for 25–60 (decision 1 (i), 2026-10-29): the closest offered is 25–54, which the
 * sentence says is not 25–60, rather than letting the label pass for the range asked.
 */
export const AGE_ONLY_SEEN =
	"X has not published its list of age buckets. This console offers two: 21–34, the reference's own example, and 25–54, which X has shown on a line item. 25–54 is not 25–60 — no bucket of that range has been shown. X allows one age bucket per line item.";

/**
 * 🔴 Whether the line item already holds an age bucket — «It is only possible to target
 * a single AGE bucket per line item» (the reference). The worker does not check a second
 * one; X refuses it. So the panel closes the age field instead of offering a request
 * that cannot succeed. `null` is a list not read, and is not «none»: the field stays
 * open and X answers, rather than the screen claiming a state it did not read.
 */
export function ageTaken(
	criteria: readonly Pick<TargetingCriterion, "targetingType">[] | null,
): boolean {
	return criteria?.some((c) => c.targetingType === "AGE") ?? false;
}

/** Beside the closed age field. Removing a criterion is on the campaign card. */
export const AGE_ALREADY =
	"This line item already has an age bucket; X allows one — remove it first.";

/** `AGE_25_TO_54` → «25–54»; a value of another shape is shown as X wrote it. */
export function ageLabel(value: string): string {
	const m = /^AGE_(\d+)_TO_(\d+)$/.exec(value);
	return m ? `${m[1]}–${m[2]}` : value;
}

/** `"1"` → «male», `"2"` → «female»; anything else as X wrote it. */
export function genderLabel(value: string): string {
	const known = GENDER_OPTIONS.find((g) => g.value === value);
	return known ? known.label.toLowerCase() : value;
}

/** A device as X describes it: maker, name and OS, so «iPhone 4» says whose list it is from. */
export function deviceLabel(d: TargetingDevice): string {
	const maker = d.manufacturer ? `${d.manufacturer} ` : "";
	const os = d.osType ? ` · ${d.osType}` : "";
	return `${maker}${d.name}${os}`;
}

/** A carrier as X describes it: its name and the country X lists it under. */
export function carrierLabel(o: TargetingNetworkOperator): string {
	return o.countryCode ? `${o.name} · ${o.countryCode}` : o.name;
}

/**
 * Beside Operating system and Device model: the one condition X puts on the two
 * together — «Users can target platforms and devices if there is no overlap»
 * (campaign-management.md:515, read 2026-10-23). X decides what overlaps; this
 * console does not, so it says so before Create rather than refusing a pair. Shown in the
 * wizard and, since 2026-10-29, beside the line item's own operating system and device fields.
 */
export const PLATFORM_DEVICE_OVERLAP =
	"X allows operating systems and devices together only if they do not overlap — its own example is BlackBerry as an operating system with iPad Air as a device. X decides what overlaps; a pair it refuses comes back as X's refusal of that criterion, and in Create ad that stops the run there.";

/** A language as X describes it: its name, and the value a criterion takes. */
export function languageLabel(l: TargetingLanguage): string {
	return `${l.name} · ${l.targetingValue}`;
}

/**
 * A non-place criterion in words — «language · en», «gender · male», «age · 25–54»,
 * «operating system · 0», «device · 1q», «carrier · 2l» (X's values: the read gives no
 * name for them) —
 * or null for a place and for a type this console does not add, which keep X's own
 * words (`criterionLabel`, and the card's country column).
 */
export function criterionKind(
	c: Pick<TargetingCriterion, "targetingType" | "targetingValue"> &
		Partial<Pick<TargetingCriterion, "name">>,
): string | null {
	// X's name when X gave one, else the value as it is — never a name built here.
	const named = c.name ?? c.targetingValue;
	switch (c.targetingType) {
		case "LANGUAGE":
			return `language · ${c.targetingValue}`;
		case "GENDER":
			return `gender · ${genderLabel(c.targetingValue)}`;
		case "AGE":
			return `age · ${ageLabel(c.targetingValue)}`;
		case "PLATFORM":
			return `operating system · ${c.targetingValue}`;
		case "DEVICE":
			return `device · ${c.targetingValue}`;
		case "NETWORK_OPERATOR":
			return `carrier · ${c.targetingValue}`;
		// PR 4: the primary types — each one widens the audience (X unions them).
		case "INTEREST":
			return `interest · ${named}`;
		case "BROAD_KEYWORD":
			return `keyword (broad) · ${c.targetingValue}`;
		case "PHRASE_KEYWORD":
			return `keyword (phrase) · ${c.targetingValue}`;
		case "EXACT_KEYWORD":
			return `keyword (exact) · ${c.targetingValue}`;
		case "SIMILAR_TO_FOLLOWERS_OF_USER":
			return `like the followers of user ${c.targetingValue}`;
		case "CUSTOM_AUDIENCE":
			return `custom audience · ${c.targetingValue}`;
		default:
			return null;
	}
}

/**
 * Beside the place search: targeted or excluded (the owner, 2026-10-29). Until then this was
 * `ONLY_INCLUDES`: «A place added here is targeted, never excluded. …». 🔴 The reference
 * names NE for keywords and app lists and does not say it applies to a place
 * (audits/2026-10-29/x-reference-targeting-v2.md), so the sentence says what is sent and that
 * X decides — never that X keeps the line item out of the place.
 */
export const PLACE_IN_OR_OUT =
	"A place is either targeted (EQ) or excluded (NE). X's reference names NE for keywords and app lists and does not say it applies to a place, so whether X takes an exclusion is X's answer, shown here as it comes. A place this line item already targets is not excluded until that criterion is removed.";

/** `created: true` on a criterion. The object exists; a retry is not the way out. */
export const CRITERION_EXISTS = {
	title: "The criterion exists — read the list before adding another.",
	body: "created: true means X made it. A second identical place narrows to the same place, so a duplicate is cheap — and since 2026-10-21 a criterion can be removed from the campaign card (Remove, beside the place), so read what is there first and remove the extra one there.",
};

/**
 * `created: "unknown"` on a criterion — and this is the inverted danger.
 *
 * A campaign or line item that may exist is a thing that may spend. A criterion
 * that may exist is a **narrowing that may not have happened**, so the line item
 * may still be reaching everyone. The sentence says that rather than borrowing a
 * warning about spend.
 */
export const CRITERION_UNKNOWN = {
	title: "The narrowing may not have happened — this line item may still reach everyone.",
	body: "X did not answer the create, so nothing here knows whether the place was added. Read this line item's criteria before adding it again; if the list is still empty, the line item is still untargeted.",
};

/** What stands on the Pause button, before it is pressed — «Stopping» until B2 (-3d, 2026-10-29). */
export const STOP_IS_SAFE =
	"Pausing asks X for PAUSED. Like starting, it is safe to repeat: asking twice asks for the same state.";

/**
 * What stands on the start button, before it is pressed.
 *
 * 🔴 **This button is new on 2026-10-08, and what stood here before was a refusal:**
 * *"This console cannot switch a line item on, and cannot delete one. Starting
 * delivery is done in X, on the account itself."* The first half was a policy of this
 * console — never a limit of X, whose field has carried both values all along — and
 * the owner lifted it on 2026-10-08. The second half — «cannot delete one» — was true
 * until 2026-10-21, when the campaign card gained Delete (twads#171 for the route, M2 for
 * the button); the sentence below now says where the delete is, and the test that held
 * «still cannot delete» was rewritten with it rather than quietly dropped.
 */
export const START_IS_SAFE =
	"Starting asks X for ACTIVE. Like pausing, it is safe to repeat: asking twice asks for the same state.";

/**
 * 🔴 Beside the start button, and the sentence this screen most needs to get right.
 *
 * `ACTIVE` here is a **weaker word than on the campaign**, and that is measured in
 * this console's own code rather than assumed: X's line item object carries neither
 * `servable` nor `reasons_not_servable` (`platform/x-api.ts`, read 2026-10-01), which
 * is why the campaign row reports both and this list reports none — see `NO_SERVABLE`
 * in `line-item-copy.ts`, which this sentence points at rather than restating.
 *
 * So the console can now say "ACTIVE" and still cannot say "delivering", and an
 * operator who reads the first as the second would wait for impressions that are not
 * coming.
 */
export const START_IS_NOT_DELIVERY =
	"ACTIVE is not a delivery signal. X reports no servable and no reason for a line item, so whether anything can serve is the campaign's answer, above: a line item ACTIVE under a paused campaign shows nothing. The path to a first impression is the campaign ACTIVE, then this line item ACTIVE, then a promoted tweet.";

/**
 * 🔴 Beside the start button: the one thing this console checks, said as a boundary
 * and not as unfinished work.
 *
 * The console reads the parent campaign and stops there on purpose (the owner's
 * boundary, 2026-10-08). A second opinion here — the campaign's status, its budget,
 * its flight — would be this console guessing at rules it has not read, and refusing
 * starts that would have worked.
 */
export const START_CHECKS_THE_PARENT =
	"Before asking, this console checks one thing: that the parent campaign still exists. Not its status, not its budget, not its dates — X refuses a start it cannot make, and you get X's reason rather than a guess from here.";

/**
 * 🔴 What a start costs when nothing narrows the line item, shown AT the start and
 * not only in a banner further down.
 *
 * The reason it cannot be a banner alone: by X's documentation a line item with no
 * criteria targets all users worldwide, and this console can now switch one on. The
 * two facts were harmless apart and are not harmless together, so the sentence is
 * put in the path of the press, with the state named.
 *
 * It asks rather than refuses — the start is the owner's decision and the console's
 * job is to name the price (the owner, via e1, 2026-10-08).
 */
export function startWarning(gate: StartGate): string | null {
	switch (gate) {
		case "ready":
			return null;
		case "untargeted":
			return "This line item has no targeting criteria, and X's documentation says such a line item targets all users worldwide — starting it now reaches everyone. Narrow it to a place above first, or press Start again to send it as it is.";
		case "excludes-only":
			// The guide: «If no targeting criteria is specified for a line item, the line item will
			// target all users worldwide» (G:573). Of a line item whose criteria only exclude, the
			// reference says nothing (audits/2026-10-29/x-reference-targeting-v2.md) — so this does
			// not say whom it reaches.
			return "Every criterion on this line item excludes (NE) — none narrows it to a place it targets. X's documentation says a line item with no targeting criteria targets all users worldwide; what X does with one whose criteria only exclude, the reference does not say. Add a place to target first, or press Start again to send it as it is.";
		case "unknown":
			return "This console could not read this line item's criteria, so it cannot tell you whether anything narrows it — and by X's documentation a line item with none reaches everyone. Read the criteria again, or press Start again to send it anyway.";
	}
}

/**
 * Where the delete is, said where someone would look for it. Until 2026-10-21 this
 * sentence was `NO_DELETE`: «This console cannot delete a line item. Stopping one leaves
 * it in X, and removing it is done in X, on the account itself.» — true then, and kept
 * here as a quote so the change reads as a change. «Stopping» became «Pausing» with B2
 * (-3d, 2026-10-29); the card is where it was.
 */
export const DELETE_IS_ON_THE_CARD =
	"Pausing a line item leaves it in X. Deleting one is on the campaign card (Delete line item, with its confirmation), and only while it is PAUSED — the reference: deleting is not reversible.";

/**
 * The sentence after a switch, built from the outcome rather than from the body.
 *
 * 🔴 **A status X reports that is not the one asked for is printed verbatim, in the
 * campaign screen's own words and with no consequence attached.** The form is
 * `mismatchText` in `campaign-copy.ts` — *"Asked for A; X reports B."* — and it is
 * borrowed here rather than reinvented so that one disagreement reads the same on
 * both screens. This screen knows what `ACTIVE` and `PAUSED` mean; a third word is X
 * saying something this console has not read, and naming its consequence would be
 * inventing one.
 *
 * 🔴 **A failure's words are the worker's, never rebuilt here.** Only the worker's
 * 502 knows the direction's consequence — a stop that did not take leaves money
 * moving, a start that did not take leaves nothing delivering — so this function
 * passes that sentence through, and when there is none it says the number rather
 * than composing a sentence it cannot support.
 */
export function switchText(outcome: StatusOutcome): string {
	switch (outcome.kind) {
		case "reported":
			if (outcome.reported !== outcome.asked)
				return `Asked for ${outcome.asked}; X reports ${outcome.reported}.`;
			return outcome.asked === "PAUSED"
				? "X reports PAUSED. Nothing serves under a paused line item."
				: "X reports ACTIVE. Whether anything can serve is the campaign's answer, above.";
		case "disagreed":
			return (
				outcome.error ??
				"HTTP 502: X took the switch and reports another status, and this answer carried no sentence to show."
			);
		case "refused":
			return outcome.error ?? `HTTP ${outcome.status}: refused, with no sentence to show.`;
		case "unreadable":
			return `HTTP ${outcome.status}: the answer carried no status to report.`;
	}
}

export const READ_ONLY =
	"You can read this line item's targeting. Narrowing it, starting it or pausing it needs write access to this ad account.";

/** A location as X describes it: its own name, level and country, and no invention. */
export function locationLabel(l: TargetingLocation): string {
	const level = l.locationType ? ` · ${l.locationType}` : "";
	const country = l.countryCode ? ` · ${l.countryCode}` : "";
	return `${l.name}${level}${country}`;
}

/**
 * A criterion as X reports it, with the operator named rather than assumed.
 *
 * `EQ` is written as "targeted", and `NE` on a place as "excluded" — the one exclusion
 * this console sends (since 2026-10-29). Any other operator, or NE on another type, is X's
 * own word: X holds a criterion someone made elsewhere, and naming it would be guessing.
 */
export function criterionLabel(c: TargetingCriterion): string {
	const where = c.locationType ? `${c.targetingValue} · ${c.locationType}` : c.targetingValue;
	const kind = criterionKind(c);
	if (kind !== null)
		return c.operatorType === "EQ" || c.operatorType === null
			? `${kind} targeted`
			: `${kind} · operator ${c.operatorType}`;
	if (c.targetingType !== "LOCATION")
		return `${c.targetingType} · ${where} · ${c.operatorType ?? "no operator"}`;
	if (c.operatorType === "NE") return `place excluded · ${where}`;
	return c.operatorType === "EQ" || c.operatorType === null
		? `place targeted · ${where}`
		: `place · ${where} · operator ${c.operatorType}`;
}

/**
 * 🔴 The table's empty sentence, which has two causes that must not read alike.
 *
 * «X holds no targeting criteria» means everyone (NO_CRITERIA above says so in red);
 * it may only be said when X answered an empty list. When the read FAILED the table
 * is just as empty, and until 2026-10-20 it printed the same sentence under the
 * danger banner — a network error rendered as a reach. The banner names the
 * failure; this sentence has to stop claiming an answer.
 */
export function criteriaEmptyText(failed: string | null): string {
	if (failed === null) return "X holds no targeting criteria for this line item.";
	return "Not read: X did not answer about this line item's targeting, so nothing here says whom it reaches.";
}

/** Under the list: what is on screen, and nothing about what is not. */
export function loadedLabel(shown: number): string {
	return `${shown} ${shown === 1 ? "criterion" : "criteria"} on this line item.`;
}
